import i18n from "i18next";
import { type FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";

import type { CampaignTerminalsTableEntity } from "src/app/features/Campaign/types/CampaignEntity";
import type { TerminalToCampaignEntity } from "src/app/features/Campaign/types/TerminalToCampaignEntity";
import type { TerminalEntity } from "src/app/features/Terminal/types/TerminalEntity";
import { getAssignedDataGroup } from "../../../../core/router/auth";
import { showWarningNotification } from "../../../../core/setup/redux/state/notifications/notificationsState";
import { isTheSameByDeepCompare } from "../../../../core/utils/utilFunctions";
import type { ExtendedColumnsType } from "../../../index";
import { SelectableTable } from "../../../Table/components/SelectableTable";
import type { AntCustomFormField, CommonFieldAttributes } from "../../types/types";
import { useLoadTerminalFromSiteIdsQuery } from "./serverApi";
import type { LoadTerminalsFromSiteIdsRequest } from "./types";

function arraysHaveSameElements(arr1: string[], arr2: string[]): boolean {
    const set2 = new Set(arr2);
    return arr1?.length === arr2?.length && arr1?.every((element) => set2?.has(element));
}

const getColumnsConfig = (t: Function): ExtendedColumnsType<TerminalToCampaignEntity>[] => {
    return [
        {
            title: t("terminal.id"),
            key: "id",
            dataIndex: "id",
        },
        {
            title: t("terminal.terminalId"),
            key: "terminalId",
            dataIndex: "terminalId",
        },
        {
            title: t("terminal.terminalType"),
            key: "terminalType",
            dataIndex: "terminalType",
        },
    ];
};

export interface CampaignTerminalsSelectableTableProps extends AntCustomFormField<CampaignTerminalsTableEntity>, CommonFieldAttributes {
    siteIds: string[];
    oldSiteIds: string[];
}

export const CampaignTerminalsSelectableTable: FC<CampaignTerminalsSelectableTableProps> = ({ value, onChange, siteIds, oldSiteIds }) => {
    const [queryConfig, setQueryConfig] = useState<LoadTerminalsFromSiteIdsRequest>({});
    const [selectedValues, setSelectedValues] = useState<TerminalToCampaignEntity[]>([]);
    const [terminalList, setTerminalList] = useState<TerminalEntity[]>([]);
    const [isInitialRender, setIsInitialRender] = useState<boolean>(true);

    const idValue = useMemo(() => value?.terminalIdList ?? [], [value?.terminalIdList]);

    useEffect(() => {
        if ((!siteIds || siteIds.length === 0) && value === undefined) {
            setTerminalList([]);
            setSelectedValues([]);
            setQueryConfig({});
        }
    }, [siteIds, value]);

    // determine if all terminals are selected
    const areAllSelected = (terminals: TerminalEntity[], selected: TerminalToCampaignEntity[]): boolean => {
        return (
            terminals.length > 0 &&
            terminals.every((terminal) => selected.some((selectedItem) => selectedItem.terminalId === terminal.terminalId))
        );
    };

    useEffect(() => {
        const dataGroup = getAssignedDataGroup();

        if (siteIds && siteIds.length > 0 && dataGroup) {
            const newConfig = { siteIds: siteIds.join(","), dataGroupCode: dataGroup.code };

            if (JSON.stringify(queryConfig) !== JSON.stringify(newConfig)) {
                setQueryConfig(newConfig);
            }
        }
    }, [queryConfig, siteIds]);

    const dispatch = useDispatch();

    useEffect(() => {
        // at least one terminal should be included in the selection to trigger onChange
        // selected values is [] when all terminals are deselected, and on initialisation
        if (onChange && selectedValues.length) {
            const selectedIds = selectedValues.map((item) => String(item.terminalId));
            if (!isTheSameByDeepCompare(idValue, selectedIds)) {
                const areAllSelectedBoolean = areAllSelected(terminalList, selectedValues);

                onChange({ terminalIdList: selectedIds, areAllTerminalsSelected: areAllSelectedBoolean });
                setIsInitialRender(false);
            }
        } else if (selectedValues.length === 0 && !isInitialRender) {
            dispatch(
                showWarningNotification({
                    text: i18n.t("campaign.error.required"),
                    autoCloseDisabled: true,
                }),
            );
        }
    }, [onChange, selectedValues, terminalList, idValue, isInitialRender, dispatch]);

    const { data } = useLoadTerminalFromSiteIdsQuery(queryConfig, { skip: !queryConfig.siteIds });

    useEffect(() => {
        if (data?.terminals) {
            // Fixes AP3ADMIN-2586
            const changedTerminalList = data.terminals.map(({ name: _name, status: _status, ...rest }: TerminalEntity) => {
                return { ...rest };
            });

            setTerminalList(changedTerminalList);
        }
    }, [data, setTerminalList]);

    useEffect(() => {
        if (terminalList) {
            const isEqualSites = arraysHaveSameElements(oldSiteIds, siteIds);
            let updatedTerminalList = terminalList;
            if (isEqualSites) {
                updatedTerminalList = terminalList.filter((terminal) => {
                    return idValue?.includes(terminal.terminalId as string);
                });
            }
            let newSelected;
            // when campaign is being created from terminalVersion, TerminalIds and SiteIds are passed from that page and should be applied on initial render.
            if (isInitialRender) {
                // if allTerminalsAreSelected flag is true on initial render (from value) then set the selected items as all terminals
                if (siteIds && value?.areAllTerminalsSelected && terminalList.length > 0) {
                    newSelected = terminalList.map((terminal) => ({ terminalId: terminal.terminalId }));
                } else {
                    // else on initial render set the selected items from terminalIdList
                    newSelected = updatedTerminalList
                        .filter((terminal) => (value?.terminalIdList ?? []).includes(terminal.terminalId as string))
                        .map((terminal) => ({ terminalId: terminal.terminalId }));
                }
            } else {
                newSelected = updatedTerminalList.map((terminal) => ({ terminalId: terminal.terminalId }));
            }

            if (JSON.stringify(newSelected) !== JSON.stringify(selectedValues)) {
                setSelectedValues(newSelected);
            }
        }
        // This method should be called when siteIds or terminalList changes,
        // but not when user manually changes the selected values, (or when the values change as a result of user changing the selectedValues)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [terminalList, oldSiteIds, siteIds]);

    return (
        <SelectableTable<TerminalToCampaignEntity>
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="id"
            dataSource={terminalList || []}
            tableData={terminalList.filter((item) => idValue.includes(String(item.terminalId)))}
            onDataChanged={setSelectedValues}
        />
    );
};
